import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Check, X, Pencil, Loader2, ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function cn(...a) { return a.filter(Boolean).join(" "); }

function FindingRow({ im, it, onDone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(it.edited_text || it.text);
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      const body = { item_id: it.id, action, ...(action === "edit" ? { text } : {}) };
      const { data } = await api.post(`/imaging/${im.id}/review`, body);
      onDone(data); setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Review failed"); }
    finally { setBusy(false); }
  };
  const status = it.status;
  return (
    <div className={cn("rounded-lg border p-2.5 text-xs", status === "accepted" ? "border-[#00A85A]/30 bg-[#F4FBF7] dark:bg-[#00A85A]/10" : status === "rejected" ? "border-red-200 bg-red-50/60 dark:border-red-500/30 dark:bg-red-500/10 opacity-70" : "border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10")} data-testid={`finding-${it.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {editing ? <Input value={text} onChange={(e) => setText(e.target.value)} className="h-8 text-xs" data-testid={`finding-edit-input-${it.id}`} />
            : <span className={cn("text-[#101516] dark:text-white", status === "rejected" && "line-through")}>{it.edited_text || it.text}</span>}
          <div className="mt-1 flex items-center gap-1.5">
            <StatusBadge status={status === "accepted" ? "success" : status === "rejected" ? "error" : "warning"} label={status === "accepted" ? (it.edited_text ? "Edited & accepted" : "Accepted") : status === "rejected" ? "Rejected" : "Pending review"} />
            {it.reviewed_by && <span className="text-[10px] text-[#8B9698]">by {it.reviewed_by}</span>}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          {editing ? (
            <>
              <Button size="sm" onClick={() => act("edit")} disabled={busy || !text.trim()} data-testid={`finding-save-${it.id}`} className="h-7 rounded-md bg-[#00A85A] hover:bg-[#008F4D] text-white px-2">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-7 rounded-md px-2">Cancel</Button>
            </>
          ) : (
            <>
              {status !== "accepted" && <Button size="sm" onClick={() => act("accept")} disabled={busy} data-testid={`finding-accept-${it.id}`} title="Accept" className="h-7 w-7 rounded-md bg-[#00A85A] hover:bg-[#008F4D] text-white p-0"><Check className="h-3.5 w-3.5" /></Button>}
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={busy} data-testid={`finding-edit-${it.id}`} title="Edit" className="h-7 w-7 rounded-md p-0"><Pencil className="h-3.5 w-3.5" /></Button>
              {status !== "rejected" && <Button size="sm" variant="outline" onClick={() => act("reject")} disabled={busy} data-testid={`finding-reject-${it.id}`} title="Reject" className="h-7 w-7 rounded-md p-0 text-red-500 hover:text-red-600"><X className="h-3.5 w-3.5" /></Button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ImagingReview({ images, onUpdate }) {
  const update = (data) => onUpdate((prev) => prev.map((x) => (x.id === data.id ? data : x)));
  const all = async (im, action) => {
    try { const { data } = await api.post(`/imaging/${im.id}/review-all`, { action }); update(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pending = images.reduce((n, im) => n + (im.review_items || []).filter((x) => x.status === "pending").length, 0);
  const analyzing = images.filter((im) => !im.ai_assessment && ["analyzing", undefined, null].includes(im.assessment_status)).length;
  if (!images.length) return null;
  return (
    <div className="space-y-3" data-testid="imaging-review">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">AI imaging findings — doctor review required</div>
        {pending > 0 ? <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600" data-testid="imaging-pending-count"><AlertTriangle className="h-3 w-3" />{pending} pending</span>
          : analyzing > 0 ? <span className="flex items-center gap-1 text-[10px] text-[#8B9698]"><Loader2 className="h-3 w-3 animate-spin" />Analyzing…</span>
          : <span className="text-[10px] font-semibold text-[#00A85A]" data-testid="imaging-all-reviewed">All findings reviewed</span>}
      </div>
      {images.map((im) => {
        const items = im.review_items || [];
        const a = im.ai_assessment;
        return (
          <div key={im.id} className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-3" data-testid={`imaging-item-${im.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="h-4 w-4 text-[#00A85A]" />
              <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-[#101516] dark:text-white truncate">{im.original_filename}</div><div className="text-[10px] text-[#8B9698]">{im.image_type}{a?.modality ? ` · ${a.modality}` : ""}{a?.quality ? ` · ${a.quality}` : ""}</div></div>
              {!a && <span className="flex items-center gap-1 text-[10px] text-[#8B9698]">{im.assessment_status === "failed" || im.assessment_status === "skipped" ? "No AI reading" : <><Loader2 className="h-3 w-3 animate-spin" />Reading…</>}</span>}
              {items.some((x) => x.status === "pending") && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => all(im, "accept")} data-testid={`imaging-accept-all-${im.id}`} className="h-7 rounded-md px-2 text-[10px]">Accept all</Button>
                  <Button size="sm" variant="outline" onClick={() => all(im, "reject")} data-testid={`imaging-reject-all-${im.id}`} className="h-7 rounded-md px-2 text-[10px] text-red-500">Reject all</Button>
                </div>
              )}
            </div>
            {a?.summary && <p className="text-[11px] text-[#687173] dark:text-[#8B9698] mb-2 italic">AI summary: {a.summary}</p>}
            {a && items.length === 0 && <p className="text-[11px] text-[#8B9698]">No discrete findings extracted.</p>}
            <div className="space-y-1.5">{items.map((it) => <FindingRow key={it.id} im={im} it={it} onDone={update} />)}</div>
          </div>
        );
      })}
    </div>
  );
}
